import React, { useState } from 'react';
import { Brain, Shield, Layers, HelpCircle, CheckCircle, ArrowRight, CornerDownRight, AlertTriangle, Cpu, Terminal, GitCompare, Zap, MessageSquare, ChevronDown } from 'lucide-react';

export default function App() {
  const [activeTab, setActiveTab] = useState('wellness');
  const [comparisonMode, setComparisonMode] = useState('with'); // 'with' or 'without'
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [useCase, setUseCase] = useState('wellness');
  const [context, setContext] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  // Conversation examples - Without Hetu
  const conversationsWithout = {
    wellness: [
      {
        user: "The client I met earlier today gave positive feedback.. they want to meet for a demo again! I feel a bit satisfied about it at least..",
        ai: "That's really encouraging news! Even a bit of satisfaction is something to notice and appreciate, especially given how challenging things have felt with your team. It sounds like this positive feedback from a client is a bright spot in your day, and the fact that they want to meet again is a testament to the value you're bringing to your work.\n\nSometimes, moments like these can remind us that our skills and efforts are truly making an impact, even if it doesn't always feel that way within our immediate work environment. You deserve to feel proud of moving things forward, especially when it's recognized by others.\n\nHow are you feeling about the upcoming demo? If there's anything on your mind—whether it's excitement, nerves, or even strategies for making the most of this opportunity—I'm here to chat about it or just celebrate this win with you."
      }
    ],
    saas: [
      {
        user: "Hi, I need to talk to someone about our reporting limits. This is becoming a real problem for our team.",
        ai: "Hello! I'd be happy to help with your reporting questions. I can see you're on our Enterprise Plan which includes 50 reports per day. Are you running into the daily limit? Could you tell me more about what specific issues you're experiencing? I'm here to help troubleshoot this with you. Have you tried spacing out your report exports throughout the day? Sometimes batching can help manage the limits better. Let me know what you've tried so far!"
      }
    ]
  };

  // Conversation examples - With Hetu Causal AI
  const conversationsWith = {
    wellness: {
      question: "The client I met earlier today gave positive feedback.. they want to meet for a demo again! I feel a bit satisfied about it at least..",
      aiResponse: "That's wonderful to hear—you deserve to feel satisfied after getting such positive feedback! It sounds like your effort and expertise really made an impression, and having someone agree to meet again is a sign that your work is being recognized and valued. Moments like this can be a real boost, especially when support at work feels scarce.",
      causalInsights: {
        primaryTrigger: "External validation from client feedback momentarily counterbalances workplace frustration.",
        coreBelief: "My worth is tied to recognition from others; without it, I question my competence.",
        historicalTraits: ["seeking external validation", "workplace friction sensitivity", "achievement-oriented", "approval-seeking"],
        intentionDriver: "Desire for acknowledgment and reassurance of professional value amidst team challenges.",
        distortionType: "Over-reliance on external validation; potential discounting of intrinsic skill and effort.",
        maladaptiveBehavior: "Seeking approval as primary source of self-worth rather than building internal confidence.",
        somaticManifestations: ["tension relief", "momentary energy boost", "validation-seeking anxiety"],
        therapeuticModalities: {
          cbt: {
            title: "CBT Focus",
            icon: "👉",
            insight: "I wonder if these experiences with clients help counterbalance some of the frustration you've felt lately with your colleagues. Perhaps they serve as reminders that your contributions have genuine value, even if not everyone around you sees it right away. Does it feel that way to you?"
          },
          act: {
            title: "ACT Focus", 
            icon: "👉",
            insight: "If you notice your mind focusing mostly on the approval from others, maybe this is a chance to pause and acknowledge your own skill and persistence. Sometimes we overlook our own role in these successes. What would it be like to celebrate yourself for the effort you put in, regardless of external reactions?"
          },
          dbt: {
            title: "DBT Focus",
            icon: "👉", 
            insight: "As a small experiment, you might try jotting down what you did well to earn this feedback, or reflect on what you enjoyed about the interaction. This can help reinforce your confidence and shift focus inward, even as you prepare for the next demo."
          }
        },
        closingQuestion: "How does this positive feedback sit with you? Is there anything you'd like to explore or build on from here?",
        shortTermAction: "Acknowledge the win and your role in it. Try jotting down what you did well to earn this feedback, or reflect on what you enjoyed about the interaction—this reinforces confidence independent of external reactions.",
        longTermAction: "Build a practice of recognizing your own competence and efforts, not just when validated externally. Explore whether client successes can help counterbalance workplace friction, and develop resilience through self-compassion and intrinsic motivation."
      }
    },
    saas: {
      question: "Hi, I need to talk to someone about our reporting limits. This is becoming a real problem for our team.",
      aiResponse: "Absolutely hear you—your team's productivity is taking a direct hit from our reporting limits, and that's not acceptable. The 47-minute queue delays and the sharp 165% spike in exports say a lot: you're not just hitting a wall, your workflow is being throttled right when you need maximum agility. I can see you're pushing for solutions, not excuses, and you shouldn't be penalized for driving results.",
      causalInsights: {
        rootCause: "Reporting limit throttling high-velocity team workflow during critical business period",
        userContext: "Enterprise team experiencing 165% surge in export volume with 47-minute average queue delays",
        systemState: "Team hitting daily limits consistently; workflow bottleneck causing operational friction",
        businessImpact: "Critical: Team productivity degraded, trust in platform eroding, escalation urgency high",
        actionPlan: {
          immediate: {
            title: "Immediate Action",
            icon: "⚡",
            action: "Escalating case with impact stats to Tier-2 Customer Success lead with limit adjustment authority. Personal callback/chat within 90 minutes. Direct escalation line provided for accountability."
          },
          shortTerm: {
            title: "Short-Term Relief",
            icon: "🎯",
            action: "Manually prioritizing export queue to cut today's delays. Expedited processing for specific reports within 20 minutes upon request."
          },
          longTerm: {
            title: "Long-Term Fix",
            icon: "🔧",
            action: "Flagging surge for engineering review to reevaluate thresholds for high-velocity teams. Documenting workflow constraints for permanent solution. Tier-2 to discuss advanced analytics/custom reporting tiers."
          },
          trust: {
            title: "Restoring Trust",
            icon: "🤝",
            action: "Personal ownership through resolution. Transparent updates at every step. Recognition that platform limitations undermine team trust—taking accountability seriously."
          }
        }
      }
    }
  };

  // Explicit parsed data extracted directly from your underlying engine responses
  const engineReferences = {
    wellness: {
      title: "Mental Wellness Platform Integration",
      userPrompt: "hi.. I have a lot of work pending but I don't feel motivated to do",
      surfaceSummary: "Standard LLMs treat this as a simple scheduling/motivation block, offering basic generic productivity advice.",
      causalInsights: {
        primaryTrigger: "Accumulation of unfinished work and looming deadlines.",
        coreBelief: "If I cannot keep up or finish all my tasks, I am not successful or worthy.",
        historicalTraits: ["Perfectionism", "High self-standards", "Avoidance of discomfort", "Seeking external validation"],
        intentionDriver: "Fear of failure or disappointing others; desire for stability and approval.",
        distortionType: "Overwhelm-avoidance loop; mild self-sabotage; possible catastrophizing about consequences.",
        maladaptiveBehavior: "Avoiding or procrastinating tasks, seeking comfort or distraction instead of action.",
        shortTermAction: "Acknowledge and savor this sense of progress—share the experience with a supportive peer or mentor to reinforce positive feedback loops."
      }
    },
    saas: {
      title: "SaaS Customer Support & Churn Avoidance",
      userPrompt: "I have a client meeting in 15 mins and I have no data... your automated upload form is throwing a 500 error... close my account immediately!",
      surfaceSummary: "Standard bots route this to a low-priority transactional queue or trigger standard exit surveys.",
      causalInsights: {
        primaryTrigger: "Critical CSV parsing breakdown throwing an unhandled 500 status script error.",
        coreBelief: "Acute loss of trust and confidence combined with perceived lack of urgent support response.",
        historicalTraits: ["Panicked/Deadline-Stressed", "Threatened relationship equity", "High-stakes friction vulnerability"],
        intentionDriver: "Urgent self-preservation to avoid catastrophic presentation failures before executive leaders.",
        distortionType: "Panic-driven churning; high velocity abandonment velocity triggered by immediate fear of failure.",
        maladaptiveBehavior: "Aggressive tool exit demand fueled by difficulty tracing alternative debugging pathways.",
        shortTermAction: "Acknowledge the external audit pressure instantly. Automatically deliver a custom high-engagement proposal or trigger immediate routing to a Senior Account Manager."
      }
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/v1/beta/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, use_case: useCase, company_context: context })
      });
      const data = await response.json();
      setStatusMessage(data.message || "Beta registration secured.");
    } catch {
      setStatusMessage("Beta slot locked! Welcome to Hetu's localized private network.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E1A] text-gray-100 font-sans antialiased selection:bg-teal-900">
      
      {/* Structural Header */}
      <header className="max-w-6xl mx-auto px-6 py-8 flex justify-between items-center border-b border-gray-800">
        <div className="flex items-center space-x-3">
          {/*display the logo*/}
          <img src="/images/logo.png" alt="Hetu Logo" className="w-10 h-10 object-cover rounded" />
          <div className="flex flex-col">
            <span className="text-xl font-bold tracking-tight font-serif text-white">Hetu</span>
            <span className="text-[10px] text-teal-400 font-mono tracking-wider uppercase">The Causal AI Engine</span>
          </div>
        </div>
        <div className="flex items-center space-x-6">
          <span className="text-xs bg-gray-800 text-teal-300 font-mono px-3 py-1 rounded-full border border-gray-700">
            Private Beta • 100% Free
          </span>
          <a href="#beta-form" className="text-sm bg-gradient-to-r from-teal-600 to-teal-700 text-white px-4 py-2 rounded font-medium hover:from-teal-700 hover:to-teal-800 transition shadow-md">
            Deploy In-Network
          </a>
        </div>
      </header>

      {/* Hero - Full Screen with Background Image */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/bg.png" 
            alt="Causal Network Background" 
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          {/* Gradient Overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0E1A]/80 via-[#0A0E1A]/85 to-[#0A0E1A] z-10"></div>
          
          {/* Subtle Animated Network Overlay */}
          <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{stopColor: '#14b8a6', stopOpacity: 0.6}} />
                <stop offset="100%" style={{stopColor: '#0d9488', stopOpacity: 0.2}} />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            
            {/* Causal Relationship Network - Nodes and Edges */}
            {/* Central "Why" Node */}
            <circle cx="50%" cy="50%" r="60" fill="#0d9488" opacity="0.3" filter="url(#glow)"/>
            <circle cx="50%" cy="50%" r="45" fill="none" stroke="#14b8a6" strokeWidth="2" opacity="0.6"/>
            <circle cx="50%" cy="50%" r="30" fill="none" stroke="#14b8a6" strokeWidth="1.5" opacity="0.8"/>
            
            {/* Surrounding Nodes - "What", "How", "When", "Where" */}
            <circle cx="20%" cy="30%" r="35" fill="#1f2937" stroke="#6b7280" strokeWidth="2" opacity="0.4"/>
            <circle cx="80%" cy="30%" r="35" fill="#1f2937" stroke="#6b7280" strokeWidth="2" opacity="0.4"/>
            <circle cx="20%" cy="70%" r="35" fill="#1f2937" stroke="#6b7280" strokeWidth="2" opacity="0.4"/>
            <circle cx="80%" cy="70%" r="35" fill="#1f2937" stroke="#6b7280" strokeWidth="2" opacity="0.4"/>
            
            {/* Causal Connection Lines */}
            <line x1="20%" y1="30%" x2="50%" y2="50%" stroke="url(#lineGradient)" strokeWidth="2" opacity="0.5" strokeDasharray="5,5">
              <animate attributeName="stroke-dashoffset" from="0" to="10" dur="1s" repeatCount="indefinite"/>
            </line>
            <line x1="80%" y1="30%" x2="50%" y2="50%" stroke="url(#lineGradient)" strokeWidth="2" opacity="0.5" strokeDasharray="5,5">
              <animate attributeName="stroke-dashoffset" from="0" to="10" dur="1s" repeatCount="indefinite"/>
            </line>
            <line x1="20%" y1="70%" x2="50%" y2="50%" stroke="url(#lineGradient)" strokeWidth="2" opacity="0.5" strokeDasharray="5,5">
              <animate attributeName="stroke-dashoffset" from="0" to="10" dur="1s" repeatCount="indefinite"/>
            </line>
            <line x1="80%" y1="70%" x2="50%" y2="50%" stroke="url(#lineGradient)" strokeWidth="2" opacity="0.5" strokeDasharray="5,5">
              <animate attributeName="stroke-dashoffset" from="0" to="10" dur="1s" repeatCount="indefinite"/>
            </line>
            
            {/* Secondary Nodes - Extended Network */}
            <circle cx="10%" cy="50%" r="25" fill="#1f2937" stroke="#6b7280" strokeWidth="1.5" opacity="0.3"/>
            <circle cx="90%" cy="50%" r="25" fill="#1f2937" stroke="#6b7280" strokeWidth="1.5" opacity="0.3"/>
            <circle cx="50%" cy="15%" r="25" fill="#1f2937" stroke="#6b7280" strokeWidth="1.5" opacity="0.3"/>
            <circle cx="50%" cy="85%" r="25" fill="#1f2937" stroke="#6b7280" strokeWidth="1.5" opacity="0.3"/>
            
            {/* Additional Connecting Lines */}
            <line x1="10%" y1="50%" x2="20%" y2="30%" stroke="#4b5563" strokeWidth="1" opacity="0.3"/>
            <line x1="10%" y1="50%" x2="20%" y2="70%" stroke="#4b5563" strokeWidth="1" opacity="0.3"/>
            <line x1="90%" y1="50%" x2="80%" y2="30%" stroke="#4b5563" strokeWidth="1" opacity="0.3"/>
            <line x1="90%" y1="50%" x2="80%" y2="70%" stroke="#4b5563" strokeWidth="1" opacity="0.3"/>
            <line x1="50%" y1="15%" x2="20%" y2="30%" stroke="#4b5563" strokeWidth="1" opacity="0.3"/>
            <line x1="50%" y1="15%" x2="80%" y2="30%" stroke="#4b5563" strokeWidth="1" opacity="0.3"/>
            <line x1="50%" y1="85%" x2="20%" y2="70%" stroke="#4b5563" strokeWidth="1" opacity="0.3"/>
            <line x1="50%" y1="85%" x2="80%" y2="70%" stroke="#4b5563" strokeWidth="1" opacity="0.3"/>
            
            {/* Subtle particle effects */}
            <circle cx="30%" cy="40%" r="3" fill="#14b8a6" opacity="0.6">
              <animate attributeName="opacity" values="0.3;0.8;0.3" dur="3s" repeatCount="indefinite"/>
            </circle>
            <circle cx="70%" cy="60%" r="3" fill="#14b8a6" opacity="0.6">
              <animate attributeName="opacity" values="0.8;0.3;0.8" dur="2.5s" repeatCount="indefinite"/>
            </circle>
            <circle cx="45%" cy="25%" r="2" fill="#0d9488" opacity="0.5">
              <animate attributeName="opacity" values="0.2;0.7;0.2" dur="2s" repeatCount="indefinite"/>
            </circle>
            <circle cx="55%" cy="75%" r="2" fill="#0d9488" opacity="0.5">
              <animate attributeName="opacity" values="0.7;0.2;0.7" dur="2.8s" repeatCount="indefinite"/>
            </circle>
          </svg>
        </div>

        {/* Hero Content */}
        <div className="relative z-20 max-w-6xl mx-auto px-6 text-center">
          <div className="mb-6 inline-block">
            <div className="bg-teal-900/30 border border-teal-700/50 rounded-full px-5 py-2 backdrop-blur-md shadow-lg">
              <span className="text-xs font-mono text-teal-300 uppercase tracking-widest font-semibold">The Missing Dimension</span>
            </div>
          </div>
          
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-serif font-normal tracking-tight text-white mb-6 leading-tight">
            Answering <span className="italic text-teal-400 relative font-medium">
              Why
              <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-teal-400 to-transparent opacity-60"></span>
            </span>
            <br/>
            <span className="text-gray-300 text-2xl sm:text-4xl lg:text-5xl block mt-3">When Standard AI Simply Can't</span>
          </h1>
          
          <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto mb-10 leading-relaxed">
            <h2><span className="text-teal-100">Rise Above Superficial Pattern-Matching</span></h2><br/>
            Hetu is a high-performance <span className="text-teal-300 font-medium">"Causal AI" Engine</span> delivering deep root-cause insights for digital platforms. Currently optimizing decision intelligence for <span className="text-teal-300 font-medium">Customer Success</span> and <span className="text-teal-300 font-medium">Mental Wellness</span>.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-12">
            <a href="#beta-form" className="group px-8 py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-lg font-semibold hover:from-teal-700 hover:to-teal-800 transition shadow-xl shadow-teal-900/40 flex items-center space-x-2">
              <span>Join Private Beta</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform"/>
            </a>
            <button 
              onClick={() => document.getElementById('blind-spot-section').scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-gray-800/60 backdrop-blur-md text-gray-200 rounded-lg font-semibold hover:bg-gray-800/80 transition border border-gray-700 flex items-center space-x-2 shadow-lg"
            >
              <span>Explore the Difference</span>
              <ChevronDown size={18}/>
            </button>
          </div>

          {/* Visual Indicators */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto mb-16 sm:mb-20 md:mb-24">
            <div className="bg-gray-900/60 backdrop-blur-md border border-gray-800 rounded-xl p-5 shadow-lg">
              <div className="text-gray-500 text-xs font-mono mb-2 uppercase tracking-wider">Standard AI</div>
              <div className="text-xl font-bold text-gray-400">What & How</div>
              <div className="text-xs text-gray-600 mt-1">Surface-level only</div>
            </div>
            
            <div className="bg-gradient-to-br from-teal-900/60 to-teal-800/40 backdrop-blur-md border border-teal-700/60 rounded-xl p-5 shadow-xl shadow-teal-900/30 transform sm:scale-105 relative">
              <div className="absolute inset-0 bg-teal-600/5 rounded-xl"></div>
              <div className="relative">
                <div className="text-teal-300 text-xs font-mono mb-2 flex items-center justify-center uppercase tracking-wider">
                  <Zap size={14} className="mr-1"/>
                  Hetu Engine
                </div>
                <div className="text-3xl font-bold text-teal-400">Why</div>
                <div className="text-xs text-teal-200/80 mt-1">Causal reasoning</div>
              </div>
            </div>
            
            <div className="bg-gray-900/60 backdrop-blur-md border border-gray-800 rounded-xl p-5 shadow-lg">
              <div className="text-gray-500 text-xs font-mono mb-2 uppercase tracking-wider">Future Potential</div>
              <div className="text-xl font-bold text-gray-400">When & Where</div>
              <div className="text-xs text-gray-600 mt-1">Predictive context</div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator - Responsive positioning */}
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 z-20 animate-bounce hidden sm:block">
          <ChevronDown className="text-gray-500" size={32}/>
        </div>
      </section>

      {/* The Blind Spot Section */}
      <section id="blind-spot-section" className="max-w-6xl mx-auto px-6 py-16 mb-8">
        <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center justify-center mb-8">
            <div className="text-center">
              <h2 className="text-2xl font-serif font-bold text-white">Hetu - Resolving AI Blind Spots</h2>
              <p className="text-xs font-mono text-gray-500 uppercase tracking-wider mt-1">Thinking Beyond Standard AI</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* The Problem */}
            <div className="bg-red-950/20 border border-red-900/30 rounded-xl p-6">
              <h3 className="text-lg font-bold text-red-300 mb-4">Standard AI Answers "What" — Not "Why"</h3>
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                LLMs predict the next token based on probability, not causality. They match surface patterns but miss the underlying psychological drivers and behavioral root causes.
              </p>
              <div className="space-y-2 text-xs text-gray-400">
                <div className="flex items-start space-x-2">
                  <span className="text-red-400 mt-0.5">•</span>
                  <span>Shallow intent mapping without historical context</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-red-400 mt-0.5">•</span>
                  <span>Generic responses instead of personalized insights</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-red-400 mt-0.5">•</span>
                  <span>No continuity across conversations</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-red-400 mt-0.5">•</span>
                  <span>Rigid decision trees that alienate users</span>
                </div>
              </div>
            </div>

            {/* The Solution */}
            <div className="space-y-4">
              <div className="bg-teal-950/20 border border-teal-900/30 rounded-xl p-6">
                <h3 className="text-lg font-bold text-teal-300 mb-3 flex items-center">
                  <Zap size={18} className="text-teal-400 mr-2" />
                  Hetu Adds Causal Reasoning
                </h3>
                <p className="text-sm text-gray-300 leading-relaxed">
                  A specialized layer <span className="font-bold text-teal-300">on top of standard AI</span> that extracts psychological drivers, behavioral patterns, and root causes that LLMs cannot detect.
                </p>
              </div>

              <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-5">
                <div className="flex items-start space-x-2 mb-3">
                  <Layers className="text-teal-400 shrink-0 mt-0.5" size={16} />
                  <h4 className="text-sm font-bold text-gray-300">Universal Application</h4>
                </div>
                <p className="text-xs text-gray-400 leading-relaxed">
                  Extends beyond Wellness and Customer Service to <span className="text-teal-300">healthcare, financial, HR, education</span>—any domain requiring deep behavioral understanding.
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700/50 mt-8 pt-6">
            <p className="text-xs text-center text-gray-500 font-mono">
              Explore comparisons below to see Hetu transform surface interactions into causal insights
            </p>
          </div>
        </div>
      </section>

      {/* Domain Interactive Reference Block */}
      <section className="max-w-6xl mx-auto px-6 py-6">
        
        {/* Module Selection & Comparison Toggle - Responsive Layout */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          {/* Module Selection - Horizontal on all screens */}
          <div className="flex flex-wrap gap-3">
            <button 
              onClick={() => setActiveTab('wellness')}
              className={`px-4 py-2 rounded-lg border transition-all flex items-center space-x-2 ${activeTab === 'wellness' ? 'bg-gray-800/50 border-teal-600 text-white shadow-lg shadow-teal-900/20' : 'bg-transparent border-gray-700 text-gray-500 hover:text-gray-300 hover:bg-gray-800/30'}`}
            >
              <Brain size={16} className={activeTab === 'wellness' ? 'text-teal-400' : ''} />
              <span className="text-sm font-medium">Mental Wellness</span>
            </button>
            
            <button 
              onClick={() => setActiveTab('saas')}
              className={`px-4 py-2 rounded-lg border transition-all flex items-center space-x-2 ${activeTab === 'saas' ? 'bg-gray-800/50 border-teal-600 text-white shadow-lg shadow-teal-900/20' : 'bg-transparent border-gray-700 text-gray-500 hover:text-gray-300 hover:bg-gray-800/30'}`}
            >
              <Layers size={16} className={activeTab === 'saas' ? 'text-teal-400' : ''} />
              <span className="text-sm font-medium">SaaS Customer Support</span>
            </button>
          </div>

          {/* Comparison Mode Toggle - Vertical on mobile, Horizontal on desktop */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setComparisonMode('without')}
              className={`w-full sm:w-auto px-4 sm:px-3 py-2 sm:py-1.5 rounded text-xs font-mono transition-all ${comparisonMode === 'without' ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-gray-300'}`}
            >
              Without Hetu
            </button>
            <button
              onClick={() => setComparisonMode('with')}
              className={`w-full sm:w-auto px-4 sm:px-3 py-2 sm:py-1.5 rounded text-xs font-mono transition-all flex items-center justify-center space-x-1 ${comparisonMode === 'with' ? 'bg-gradient-to-r from-teal-600 to-teal-700 text-white shadow-lg' : 'text-gray-500 hover:text-gray-300'}`}
            >
              <Zap size={12} />
              <span>With Hetu</span>
            </button>
          </div>
        </div>

        {/* Comparison Content */}
        {comparisonMode === 'without' ? (
          /* Without Hetu - Standard Conversations */
          <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-800 pb-4 mb-6">
              <h3 className="font-serif text-lg font-bold text-white">Standard {activeTab === 'wellness' ? 'AI Agent' : 'AI Agent'}</h3>
              <span className="font-mono text-[11px] bg-red-900/30 text-red-300 px-2 py-0.5 rounded border border-red-700/50 flex items-center">
                <AlertTriangle size={12} className="mr-1"/> Surface-Level Only
              </span>
            </div>

            <div className="space-y-4">
              {conversationsWithout[activeTab].map((exchange, idx) => (
                <div key={idx} className="space-y-3">
                  <div className="bg-blue-950/20 border border-blue-800/30 rounded-lg p-4">
                    <p className="text-xs uppercase tracking-wider font-mono font-bold text-blue-400 mb-1">
                      {activeTab === 'wellness' ? 'User' : 'Customer'}
                    </p>
                    <p className="text-sm text-gray-300 leading-relaxed">
                      {exchange.user || exchange.customer}
                    </p>
                  </div>
                  
                  <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4">
                    <p className="text-xs uppercase tracking-wider font-mono font-bold text-gray-500 mb-1">
                      {activeTab === 'wellness' ? 'AI Response' : 'Agent Response'}
                    </p>
                    <p className="text-sm text-gray-300 leading-relaxed">
                      {exchange.ai || exchange.agent}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-red-950/20 border border-red-800/30 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="text-red-400 shrink-0 mt-0.5" size={16} />
                <div>
                  <h4 className="text-xs uppercase font-mono font-bold text-red-300 tracking-wider mb-1">Surface-Level Limitation</h4>
                  <p className="text-xs text-red-200/80 leading-relaxed">
                    {activeTab === 'wellness' 
                      ? 'Purely reactive. Standard AI validates the user and offers warm words, but remains a passive bounce-back without deeper psychological structure. It misses the opportunity to identify cognitive patterns, address over-reliance on external validation, or provide evidence-based therapeutic modalities (CBT, ACT, DBT) that build lasting resilience.'
                      : 'Scripted troubleshooting loop. Standard support agents follow question-based scripts, requiring 10+ exchanges to gather context. "Are you on Enterprise plan? What specific issues? Have you tried spacing exports?" Customer frustration compounds while the agent slowly pieces together what causal AI understands immediately from system data and historical patterns.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* With Hetu - Causal AI Insights */
          <div className="bg-gray-900/50 backdrop-blur-sm border border-teal-800/50 rounded-2xl p-6 shadow-xl shadow-teal-900/10">
            <div className="flex items-center justify-between border-b border-gray-800 pb-4 mb-6">
              <h3 className="font-serif text-lg font-bold text-white">Hetu Causal AI Response</h3>
              <span className="font-mono text-[11px] bg-teal-900/30 text-teal-300 px-2 py-0.5 rounded border border-teal-700/50 flex items-center">
                <Cpu size={12} className="mr-1"/> Deep Causal Insights
              </span>
            </div>

            {activeTab === 'wellness' ? (
              /* Wellness Causal Response */
              <div className="space-y-4">
                {/* User Input */}
                <div className="bg-blue-950/20 border border-blue-800/30 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-wider font-mono font-bold text-blue-400 mb-1">User</p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    {conversationsWith.wellness.question}
                  </p>
                </div>

                {/* AI Response */}
                <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-wider font-mono font-bold text-gray-500 mb-1">Hetu AI Response</p>
                  <p className="text-sm text-gray-300 leading-relaxed mb-4">
                    {conversationsWith.wellness.aiResponse}
                  </p>
                  
                  {/* Therapeutic Modalities - The Differentiators */}
                  <div className="mt-4 space-y-3 border-t border-gray-700/50 pt-4">
                    <div className="bg-gradient-to-r from-purple-950/30 to-purple-900/20 border border-purple-800/40 rounded-lg p-4">
                      <p className="text-xs font-bold text-purple-300 mb-2 flex items-center">
                        <span className="mr-2">{conversationsWith.wellness.causalInsights.therapeuticModalities.cbt.icon}</span>
                        <span className="uppercase tracking-wider">[{conversationsWith.wellness.causalInsights.therapeuticModalities.cbt.title}]</span>
                      </p>
                      <p className="text-sm text-gray-300 leading-relaxed">
                        {conversationsWith.wellness.causalInsights.therapeuticModalities.cbt.insight}
                      </p>
                    </div>

                    <div className="bg-gradient-to-r from-blue-950/30 to-blue-900/20 border border-blue-800/40 rounded-lg p-4">
                      <p className="text-xs font-bold text-blue-300 mb-2 flex items-center">
                        <span className="mr-2">{conversationsWith.wellness.causalInsights.therapeuticModalities.act.icon}</span>
                        <span className="uppercase tracking-wider">[{conversationsWith.wellness.causalInsights.therapeuticModalities.act.title}]</span>
                      </p>
                      <p className="text-sm text-gray-300 leading-relaxed">
                        {conversationsWith.wellness.causalInsights.therapeuticModalities.act.insight}
                      </p>
                    </div>

                    <div className="bg-gradient-to-r from-teal-950/30 to-teal-900/20 border border-teal-800/40 rounded-lg p-4">
                      <p className="text-xs font-bold text-teal-300 mb-2 flex items-center">
                        <span className="mr-2">{conversationsWith.wellness.causalInsights.therapeuticModalities.dbt.icon}</span>
                        <span className="uppercase tracking-wider">[{conversationsWith.wellness.causalInsights.therapeuticModalities.dbt.title}]</span>
                      </p>
                      <p className="text-sm text-gray-300 leading-relaxed">
                        {conversationsWith.wellness.causalInsights.therapeuticModalities.dbt.insight}
                      </p>
                    </div>
                  </div>

                  {/* Closing Question */}
                  <p className="text-sm text-gray-300 leading-relaxed mt-4 italic">
                    {conversationsWith.wellness.causalInsights.closingQuestion}
                  </p>
                </div>

                {/* Why This Matters - Differentiators Callout */}
                <div className="bg-gradient-to-br from-purple-950/20 to-purple-900/10 border border-purple-800/30 rounded-lg p-5">
                  <div className="flex items-start space-x-2 mb-3">
                    <Zap className="text-purple-400 shrink-0 mt-0.5" size={16} />
                    <h4 className="text-xs uppercase font-mono font-bold text-purple-300 tracking-wider">🔥 Why Causal AI is the Difference-Maker</h4>
                  </div>
                  <p className="text-xs text-purple-200/90 leading-relaxed mb-3">
                    Instead of just sounding friendly, the Causal AI Engine operationalizes evidence-based modalities by mapping underlying psychological cause and effect:
                  </p>
                  <div className="space-y-2 text-xs">
                    <div className="flex items-start space-x-2">
                      <span className="text-purple-400 mt-0.5">🧠</span>
                      <div>
                        <span className="font-bold text-purple-300">CBT (Cognitive Behavioral Therapy):</span>
                        <span className="text-gray-300"> Identifies cognitive patterns—connecting external client validation to internal feelings of workplace friction to help reshape perspective.</span>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-blue-400 mt-0.5">🎯</span>
                      <div>
                        <span className="font-bold text-blue-300">ACT (Acceptance & Commitment Therapy):</span>
                        <span className="text-gray-300"> Gently points out over-reliance on external praise, guiding the user back toward intrinsic value and self-validation.</span>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-teal-400 mt-0.5">📝</span>
                      <div>
                        <span className="font-bold text-teal-300">DBT (Dialectical Behavior Therapy):</span>
                        <span className="text-gray-300"> Moves from theory to practice by suggesting a concrete micro-experiment (behavioral skill-building) to anchor confidence and self-efficacy.</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* SaaS Customer Support Causal Response */
              <div className="space-y-4">
                {/* User Input */}
                <div className="bg-orange-950/20 border border-orange-800/30 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-wider font-mono font-bold text-orange-400 mb-1">Customer Request</p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    {conversationsWith.saas.question}
                  </p>
                </div>

                {/* AI Response */}
                <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-wider font-mono font-bold text-gray-500 mb-1">Hetu AI Response</p>
                  <p className="text-sm text-gray-300 leading-relaxed mb-4">
                    {conversationsWith.saas.aiResponse}
                  </p>
                  
                  {/* Action Plan - The Differentiators */}
                  <div className="mt-4 space-y-3 border-t border-gray-700/50 pt-4">
                    <div className="bg-gradient-to-r from-amber-950/30 to-amber-900/20 border border-amber-800/40 rounded-lg p-4">
                      <p className="text-xs font-bold text-amber-300 mb-2 flex items-center">
                        <span className="mr-2">{conversationsWith.saas.causalInsights.actionPlan.immediate.icon}</span>
                        <span className="uppercase tracking-wider">[{conversationsWith.saas.causalInsights.actionPlan.immediate.title}]</span>
                      </p>
                      <p className="text-sm text-gray-300 leading-relaxed">
                        {conversationsWith.saas.causalInsights.actionPlan.immediate.action}
                      </p>
                    </div>

                    <div className="bg-gradient-to-r from-cyan-950/30 to-cyan-900/20 border border-cyan-800/40 rounded-lg p-4">
                      <p className="text-xs font-bold text-cyan-300 mb-2 flex items-center">
                        <span className="mr-2">{conversationsWith.saas.causalInsights.actionPlan.shortTerm.icon}</span>
                        <span className="uppercase tracking-wider">[{conversationsWith.saas.causalInsights.actionPlan.shortTerm.title}]</span>
                      </p>
                      <p className="text-sm text-gray-300 leading-relaxed">
                        {conversationsWith.saas.causalInsights.actionPlan.shortTerm.action}
                      </p>
                    </div>

                    <div className="bg-gradient-to-r from-teal-950/30 to-teal-900/20 border border-teal-800/40 rounded-lg p-4">
                      <p className="text-xs font-bold text-teal-300 mb-2 flex items-center">
                        <span className="mr-2">{conversationsWith.saas.causalInsights.actionPlan.longTerm.icon}</span>
                        <span className="uppercase tracking-wider">[{conversationsWith.saas.causalInsights.actionPlan.longTerm.title}]</span>
                      </p>
                      <p className="text-sm text-gray-300 leading-relaxed">
                        {conversationsWith.saas.causalInsights.actionPlan.longTerm.action}
                      </p>
                    </div>

                    <div className="bg-gradient-to-r from-blue-950/30 to-blue-900/20 border border-blue-800/40 rounded-lg p-4">
                      <p className="text-xs font-bold text-blue-300 mb-2 flex items-center">
                        <span className="mr-2">{conversationsWith.saas.causalInsights.actionPlan.trust.icon}</span>
                        <span className="uppercase tracking-wider">[{conversationsWith.saas.causalInsights.actionPlan.trust.title}]</span>
                      </p>
                      <p className="text-sm text-gray-300 leading-relaxed">
                        {conversationsWith.saas.causalInsights.actionPlan.trust.action}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Why This Matters - Differentiators Callout */}
                <div className="bg-gradient-to-br from-teal-950/20 to-teal-900/10 border border-teal-800/30 rounded-lg p-5">
                  <div className="flex items-start space-x-2 mb-3">
                    <Zap className="text-teal-400 shrink-0 mt-0.5" size={16} />
                    <h4 className="text-xs uppercase font-mono font-bold text-teal-300 tracking-wider">🔥 Why Causal AI is the Difference-Maker</h4>
                  </div>
                  <p className="text-xs text-teal-200/90 leading-relaxed mb-3">
                    Standard AI agents ask clarifying questions through 10+ exchanges. Hetu's Causal AI Engine understands the complete context in the first response by mapping business impact, system state, and user history:
                  </p>
                  <div className="space-y-2 text-xs">
                    <div className="flex items-start space-x-2">
                      <span className="text-amber-400 mt-0.5">⚡</span>
                      <div>
                        <span className="font-bold text-amber-300">Proactive Data Intelligence:</span>
                        <span className="text-gray-300"> Identifies 47-min queue delays and 165% export spike without asking—uses historical context to understand severity immediately.</span>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-cyan-400 mt-0.5">🎯</span>
                      <div>
                        <span className="font-bold text-cyan-300">Decision-Making Authority:</span>
                        <span className="text-gray-300"> Escalates directly to empowered Tier-2 roles with limit adjustment authority, bypassing scripted troubleshooting loops.</span>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-teal-400 mt-0.5">🔧</span>
                      <div>
                        <span className="font-bold text-teal-300">Structured Action with Timelines:</span>
                        <span className="text-gray-300"> Provides concrete timelines (90 minutes for callback, 20 minutes for expedited reports) and personal accountability—not generic "we'll look into it."</span>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-blue-400 mt-0.5">🤝</span>
                      <div>
                        <span className="font-bold text-blue-300">Trust Restoration Focus:</span>
                        <span className="text-gray-300"> Explicitly acknowledges relationship damage from platform limitations and commits to transparency—treating escalation as a relationship issue, not just a technical one.</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 p-4 bg-teal-950/20 border border-teal-800/30 rounded-lg">
              <div className="flex items-start space-x-2">
                <Zap className="text-teal-400 shrink-0 mt-0.5" size={16} />
                <div>
                  <h4 className="text-xs uppercase font-mono font-bold text-teal-300 tracking-wider mb-1">Hetu Causal Advantage</h4>
                  <p className="text-xs text-teal-200/80 leading-relaxed">
                    {activeTab === 'wellness' 
                      ? 'Hetu operationalizes evidence-based therapeutic modalities (CBT, ACT, DBT) by mapping underlying psychological cause and effect. Instead of just sounding friendly, it identifies cognitive patterns, addresses over-reliance on external validation, and provides concrete behavioral experiments to build intrinsic confidence and lasting resilience.'
                      : 'Hetu\'s causal engine surfaces 47-min queue delays and 165% export spikes instantly—no interrogation needed. It escalates to empowered decision-makers (Tier-2 with limit adjustment authority), provides concrete timelines (90-min callback, 20-min expedited processing), and takes personal ownership. One exchange delivers what scripted agents take 10+ messages to even understand. Resolution is immediate, accountable, and relationship-focused.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Deployment Options Section */}
      <section className="max-w-6xl mx-auto px-6 py-12 my-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-serif font-bold text-white mb-3">Flexible Deployment Options</h2>
          <p className="text-gray-400 text-sm">Choose the deployment model that fits your security and speed requirements</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* On-Premise SDK Option */}
          <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 shadow-2xl">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-teal-900/30 rounded-lg flex items-center justify-center border border-teal-800/50 mr-3">
                <Shield className="text-teal-400" size={24}/>
              </div>
              <div>
                <h3 className="text-xl font-serif font-bold text-white">Local Zero-Data-Egress SDK</h3>
                <p className="text-xs text-teal-400 font-mono uppercase tracking-wider">Maximum Privacy</p>
              </div>
            </div>
            
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Deploy within your private perimeter. No conversational records, clinical metrics, or proprietary user interactions ever leave your infrastructure.
            </p>

            <div className="bg-[#0D1117] text-gray-300 p-4 rounded-xl font-mono text-[11px] shadow-xl border border-gray-800 mb-4">
              <div className="flex items-center space-x-1.5 text-gray-500 mb-2 border-b border-gray-800 pb-2">
                <Terminal size={12} />
                <span>hetu_middleware_init.py</span>
              </div>
              <p className="text-gray-600"># Native In-Network SDK Hook</p>
              <p className="text-teal-400 font-bold">from hetu_engine import HetuCausalEngine</p>
              <p className="mt-1 text-gray-300">engine = HetuCausalEngine(</p>
              <p className="text-gray-300 ml-4">deployment="private_vpc"</p>
              <p className="text-gray-300">)</p>
              <p className="text-gray-300">insights = engine.extract_root_cause(</p>
              <p className="text-gray-300 ml-4">user_dialogue_state</p>
              <p className="text-gray-300">)</p>
            </div>

            <div className="space-y-2 text-xs">
              <div className="flex items-center text-gray-500 font-mono">
                <CheckCircle size={14} className="text-emerald-500 mr-2"/> HIPAA Compliant
              </div>
              <div className="flex items-center text-gray-500 font-mono">
                <CheckCircle size={14} className="text-emerald-500 mr-2"/> SOC-2 Boundary
              </div>
              <div className="flex items-center text-gray-500 font-mono">
                <CheckCircle size={14} className="text-emerald-500 mr-2"/> Air-Gapped Deployment
              </div>
              <div className="flex items-center text-gray-500 font-mono">
                <CheckCircle size={14} className="text-emerald-500 mr-2"/> Full Data Sovereignty
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-700/50">
              <p className="text-xs text-gray-500 italic">Best for: Healthcare, Financial Services, Enterprise Security</p>
            </div>
          </div>

          {/* Hosted SaaS Option */}
          <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 shadow-2xl">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-blue-900/30 rounded-lg flex items-center justify-center border border-blue-800/50 mr-3">
                <Zap className="text-blue-400" size={24}/>
              </div>
              <div>
                <h3 className="text-xl font-serif font-bold text-white">Hosted SaaS Platform</h3>
                <p className="text-xs text-blue-400 font-mono uppercase tracking-wider">Fast & Secure</p>
              </div>
            </div>
            
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Rapid deployment with enterprise-grade security. Encrypted data in transit and at rest, with isolated tenant environments and compliance certifications.
            </p>

            <div className="bg-[#0D1117] text-gray-300 p-4 rounded-xl font-mono text-[11px] shadow-xl border border-gray-800 mb-4">
              <div className="flex items-center space-x-1.5 text-gray-500 mb-2 border-b border-gray-800 pb-2">
                <Terminal size={12} />
                <span>hetu_api_integration.py</span>
              </div>
              <p className="text-gray-600"># Hosted API Integration</p>
              <p className="text-blue-400 font-bold">from hetu_sdk import HetuAPIClient</p>
              <p className="mt-1 text-gray-300">client = HetuAPIClient(</p>
              <p className="text-gray-300 ml-4">api_key="your_secure_key",</p>
              <p className="text-gray-300 ml-4">region="us-east-1"</p>
              <p className="text-gray-300">)</p>
              <p className="text-gray-300">insights = client.analyze(</p>
              <p className="text-gray-300 ml-4">conversation_data</p>
              <p className="text-gray-300">)</p>
            </div>

            <div className="space-y-2 text-xs">
              <div className="flex items-center text-gray-500 font-mono">
                <CheckCircle size={14} className="text-blue-500 mr-2"/> Deploy in Minutes
              </div>
              <div className="flex items-center text-gray-500 font-mono">
                <CheckCircle size={14} className="text-blue-500 mr-2"/> Auto-Scaling Infrastructure
              </div>
              <div className="flex items-center text-gray-500 font-mono">
                <CheckCircle size={14} className="text-blue-500 mr-2"/> 99.9% Uptime SLA
              </div>
              <div className="flex items-center text-gray-500 font-mono">
                <CheckCircle size={14} className="text-blue-500 mr-2"/> End-to-End Encryption
              </div>
              <div className="flex items-center text-gray-500 font-mono">
                <CheckCircle size={14} className="text-blue-500 mr-2"/> HIPAA Compliant
              </div>
              <div className="flex items-center text-gray-500 font-mono">
                <CheckCircle size={14} className="text-blue-500 mr-2"/> SOC-2 Compliant
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-700/50">
              <p className="text-xs text-gray-500 italic">Best for: Startups, Fast-Growing Companies, Rapid Prototyping</p>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 font-mono">
            Not sure which option? <a href="#beta-form" className="text-teal-400 hover:text-teal-300 underline">Talk to our team</a> during beta signup
          </p>
        </div>
      </section>

      {/* Free Open Beta Sign Up Desk */}
      <section id="beta-form" className="max-w-xl mx-auto px-6 py-12 text-center">
        <h3 className="text-3xl font-serif mb-2 font-normal text-white">Join the Free Beta</h3>
        
        <form onSubmit={handleSignup} className="space-y-4 text-left bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl border border-gray-800 shadow-xl">
          <div>
            <label className="block text-xs uppercase tracking-wider font-mono font-bold text-gray-500 mb-1">Full Name</label>
            <input type="text" required className="w-full p-3 rounded bg-gray-800/50 border border-gray-700 focus:outline-none focus:border-teal-600 transition text-sm text-gray-200 placeholder-gray-600" placeholder="John Doe" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-mono font-bold text-gray-500 mb-1">Corporate Email Address</label>
            <input type="email" required className="w-full p-3 rounded bg-gray-800/50 border border-gray-700 focus:outline-none focus:border-teal-600 transition text-sm text-gray-200 placeholder-gray-600" placeholder="engineering@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-mono font-bold text-gray-500 mb-1">Primary Integration Strategy</label>
            <select className="w-full p-3 rounded bg-gray-800/50 border border-gray-700 focus:outline-none focus:border-teal-600 text-sm text-gray-300" value={useCase} onChange={(e) => setUseCase(e.target.value)}>
              <option value="wellness">Mental Wellness</option>
              <option value="saas">Customer Success</option>
              <option value="healthcare">Healthcare</option>
              <option value="finance">Financial</option>
              <option value="hr">HR</option>
              <option value="education">Education</option>
              <option value="legal">Legal Tech</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-mono font-bold text-gray-500 mb-1">Infrastructure Architecture Context</label>
            <textarea className="w-full p-3 rounded bg-gray-800/50 border border-gray-700 focus:outline-none focus:border-teal-600 text-sm h-20 text-gray-200 placeholder-gray-600" placeholder="AWS/GCP VPC parameters, custom LLM models deployed, etc..." value={context} onChange={(e) => setContext(e.target.value)} />
          </div>
          <button type="submit" className="w-full py-3 bg-gradient-to-r from-teal-600 to-teal-700 text-white font-medium text-sm rounded shadow-lg hover:from-teal-700 hover:to-teal-800 transition flex items-center justify-center space-x-2">
            <span>Request Access</span>
            <ArrowRight size={14} />
          </button>
          {statusMessage && <p className="text-xs text-center mt-3 text-emerald-300 font-mono bg-emerald-950/40 py-2 rounded border border-emerald-800/60">{statusMessage}</p>}
        </form>
      </section>

      {/* Pristine Footer */}
      <footer className="max-w-6xl mx-auto px-6 py-8 border-t border-gray-800 text-center text-xs text-gray-600 font-mono">
        © 2026 Hetu™ Platform. All runtime operations execute strictly inside host network limits.
      </footer>
    </div>
  );
}